import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Languages, MessageSquare, Mic } from 'lucide-react';
import { useLocation } from 'wouter';

type Mode = 'dictation' | 'conversation' | 'interpreter';

interface ModeSwitcherProps {
  activeMode: Mode;
  disabled?: boolean;
}

const activeClasses = 'bg-white text-primary shadow-sm';
const inactiveClasses = 'text-muted-foreground hover:text-foreground';

export const ModeSwitcher = ({ activeMode, disabled = false }: ModeSwitcherProps) => {
  const [, navigate] = useLocation();

  const { authAccountId, authOrgId, isViewingAsDelegate } = useAuth();
  const { data: availability } = useQuery({
    queryKey: ['interpreter-availability', authAccountId, authOrgId],
    queryFn: async (): Promise<{ enabled: boolean }> =>
      (await apiRequest('GET', '/api/interpreter/availability')).json(),
    enabled: !!authAccountId && !isViewingAsDelegate,
    retry: false,
    staleTime: 30_000,
  });

  return (
    <div className="flex h-12 rounded-lg bg-slate-100 p-1">
      <button
        type="button"
        disabled={disabled}
        aria-pressed={activeMode === 'dictation'}
        onClick={activeMode === 'dictation' ? undefined : () => navigate('/dictation/new?modeSwitch=true')}
        className={cn(
          'flex w-15 cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-xs transition-all sm:w-auto md:gap-2 md:px-3 md:py-1.5 md:text-sm',
          activeMode === 'dictation' ? activeClasses : inactiveClasses
        )}
      >
        <Mic className="mx-auto h-4 w-4" />
        <span className="hidden sm:inline">Diktering</span>
      </button>
      <button
        type="button"
        disabled={disabled}
        aria-pressed={activeMode === 'conversation'}
        onClick={activeMode === 'conversation' ? undefined : () => navigate('/conversation/new?modeSwitch=true')}
        className={cn(
          'flex w-15 cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-xs transition-all sm:w-auto md:gap-2 md:px-3 md:py-1.5 md:text-sm',
          activeMode === 'conversation' ? activeClasses : inactiveClasses
        )}
      >
        <MessageSquare className="mx-auto h-4 w-4" />
        <span className="hidden sm:inline">Viðtal</span>
      </button>
      {(activeMode === 'interpreter' || (!isViewingAsDelegate && availability?.enabled)) && (
        <button
          type="button"
          disabled={disabled}
          aria-label="Túlkunarviðtal Beta"
          aria-pressed={activeMode === 'interpreter'}
          onClick={activeMode === 'interpreter' ? undefined : () => navigate('/interpreter')}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-xs transition-all md:gap-2 md:px-3 md:text-sm',
            activeMode === 'interpreter' ? activeClasses : inactiveClasses
          )}
        >
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">Túlkunarviðtal</span>
          <span className="rounded bg-primary/10 px-1 py-0.5 text-[10px] text-primary">Beta</span>
        </button>
      )}
    </div>
  );
};
